import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, type ReactElement } from "react";
import Error from "~/components/error/Error";
import { FullWidthContainer } from "~/components/layout/Container";
import Layout from "~/components/layout/Layout";
import fetchJson, { type FetchError } from "~/lib/fetchJson";
import useUser from "~/lib/useUser";
import { type User } from "../api/auth/user";

const AuthCallbackPage = () => {
  //get the token from params
  const { push, query, replace } = useRouter();
  const { token, refreshToken, error } = query;

  const { mutateUser } = useUser();

  useEffect(() => {
    if (!token || !refreshToken) return;
    fetchJson("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, refreshToken }),
    })
      .then(async (user) => {
        const tempUser = user as User;
        await mutateUser(tempUser);

        if (tempUser?.isLoggedIn && !tempUser?.userConsent) {
          await replace("/terms-compact");
        } else {
          await push("/");
        }
      })
      .catch(async (error: FetchError) => {
        await mutateUser(undefined);
        console.error("An unexpected error happened:", error);
      });
  }, [token, mutateUser, push, refreshToken, error, replace]);

  if (error) {
    return (
      <FullWidthContainer>
        <Error errorTitle={error.toString()} />
      </FullWidthContainer>
    );
  }

  return (
    <>
      <Head>
        <title>RFOX VALT | Redirecting...</title>
      </Head>
      <FullWidthContainer>
        <div className="flex h-full animate-pulse space-x-4">
          <div className="flex-1 space-y-6 py-1">
            <div className="h-4 rounded bg-dark-100"></div>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 h-4 rounded bg-dark-100"></div>
                <div className="col-span-1 h-4 rounded bg-dark-100"></div>
              </div>
              <div className="h-4 rounded bg-dark-100"></div>
            </div>
          </div>
        </div>
      </FullWidthContainer>
    </>
  );
};

AuthCallbackPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout publicAccess={true}>{page}</Layout>;
};

export default AuthCallbackPage;
